import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import ConfirmationModal from '../../components/admin/ConfirmationModal';
import SafetyActionModal from '../../components/admin/SafetyActionModal';
import EvidenceModal from '../../components/admin/EvidenceModal';
import { buildApiUrl } from '../../config/api';
import { FiAlertTriangle, FiCheck, FiSlash, FiTrash2, FiClock, FiEye, FiShield } from 'react-icons/fi';
import { fetchJson } from '../../utils/fetchJson';
import { useSocket } from '../../context/SocketContext';

const AdminReports = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);

    const [filter, setFilter] = useState('pending'); // pending, resolved, dismissed, all

    // Modal State
    const [modalConfig, setModalConfig] = useState({
        isOpen: false,
        type: '', // 'resolve', 'dismiss', 'suspend', 'delete', 'deletePost'
        title: '',
        message: '',
        reportId: null,
        userId: null,
        postId: null,
        isDestructive: false,
        showInput: false,
        inputPlaceholder: '',
        inputValue: '',
        confirmText: 'Confirm'
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Safety Modal State
    const [safetyModalOpen, setSafetyModalOpen] = useState(false);
    const [selectedSafeyReport, setSelectedSafetyReport] = useState(null);

    // Evidence Modal State
    const [evidenceModalOpen, setEvidenceModalOpen] = useState(false);
    const [selectedEvidence, setSelectedEvidence] = useState(null);

    const { socket } = useSocket();

    useEffect(() => {
        const fetchReports = async () => {

            try {
                const token = localStorage.getItem('token');
                // Fetch reports with current filter
                const { data } = await fetchJson(buildApiUrl(`/api/admin/reports?status=${filter === 'all' ? '' : filter}`), {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (data.success) {
                    const fetchedReports = data.data.reports || [];
                    setReports(fetchedReports);
                }
            } catch (err) {
                console.error('Error fetching reports:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchReports();
    }, [filter]);

    // Real-time updates
    useEffect(() => {
        if (!socket) return;

        const handleNewReport = (newReport) => {
            console.log('🔔 New report received:', newReport);
            // Only add if it matches current filter (or if filter is 'all'/'pending')
            // And prevent duplicates
            if (filter === 'all' || filter === 'pending') {
                setReports(prev => {
                    if (prev.find(r => r._id === newReport._id)) return prev;
                    return [newReport, ...prev];
                });
                // Optional: Show toast or notification
            }
        };

        socket.on('new_report', handleNewReport);

        return () => {
            socket.off('new_report', handleNewReport);
        };
    }, [socket, filter]);

    // Open Modal Handlers
    const openStatusModal = (reportId, newStatus) => {
        const isResolve = newStatus === 'resolved';
        setModalConfig({
            isOpen: true,
            type: isResolve ? 'resolve' : 'dismiss',
            title: isResolve ? 'Mark as Resolved' : 'Dismiss Report',
            message: `Are you sure you want to mark this report as ${isResolve ? 'Resolved' : 'Dismissed'}?`,
            reportId,
            isDestructive: false,
            confirmText: isResolve ? 'Resolve' : 'Dismiss',
            showInput: false,
            inputValue: ''
        });
    };

    const openSuspendModal = (userId) => {
        setModalConfig({
            isOpen: true,
            type: 'suspend',
            title: 'Suspend User',
            message: 'This will temporarily block the user from accessing their account.',
            userId,
            isDestructive: true,
            confirmText: 'Suspend User',
            showInput: true,
            inputPlaceholder: 'Enter reason for suspension...',
            inputValue: ''
        });
    };

    const openDeleteModal = (userId) => {
        setModalConfig({
            isOpen: true,
            type: 'delete',
            title: 'Delete User',
            message: 'ARE YOU SURE? This will permanently delete the user and all their data. This action cannot be undone.',
            userId,
            isDestructive: true,
            confirmText: 'Permanently Delete',
            showInput: false,
            inputValue: ''
        });
    };

    const openDeletePostModal = (postId, reportId) => {
        setModalConfig({
            isOpen: true,
            type: 'deletePost',
            title: 'Delete Post',
            message: 'Are you sure you want to delete this post? This action cannot be undone.',
            postId,
            reportId,
            isDestructive: true,
            confirmText: 'Delete Post',
            showInput: false,
            inputValue: ''
        });
    };

    const openSuccessModal = (message) => {
        setModalConfig({
            isOpen: true,
            type: 'success',
            title: 'Success',
            message: message,
            confirmText: 'OK',
            cancelText: null, // Hides the cancel button
            isDestructive: false,
            showInput: false
        });
    };

    const openErrorModal = (message) => {
        setModalConfig({
            isOpen: true,
            type: 'allow_error', // distinct from internal error states if needed
            title: 'Error',
            message: message,
            confirmText: 'OK',
            cancelText: null,
            isDestructive: false,
            showInput: false
        });
    };

    const closeModal = () => {
        setModalConfig(prev => ({ ...prev, isOpen: false }));
        setSafetyModalOpen(false);
        setEvidenceModalOpen(false);
        setIsSubmitting(false);
    };

    const openSafetyModal = (report) => {
        setSelectedSafetyReport(report);
        setSafetyModalOpen(true);
    };

    const handleSafetyAction = async (actionData) => {
        setIsSubmitting(true);
        const token = localStorage.getItem('token');
        try {
            const { action, ncmecReportId, moderatorNotes, banReason, reportingStatus, agencyName } = actionData;
            const res = await fetchJson(buildApiUrl(`/api/admin/reports/${selectedSafeyReport._id}/resolve-safety`), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action, ncmecReportId, moderatorNotes, banReason, reportingStatus, agencyName })
            });

            if (res.data.data.supervisorApprovalRequired) {
                openSuccessModal('Request submitted for Supervisor Approval. User has been suspended and evidence preserved.');
            } else {
                openSuccessModal('Safety Action Completed & Evidence Preserved');
            }

            // Update local state
            setReports(prevReports =>
                prevReports.map(report =>
                    report._id === selectedSafeyReport._id
                        ? { ...report, ...res.data.data } // Merge updated report fields (status, etc)
                        : report
                )
            );
        } catch (err) {
            console.error('Safety action failed:', err);
            openErrorModal('Failed to process safety action');
        } finally {
            closeModal();
        }
    };

    const handleViewEvidence = async (reportId) => {
        const token = localStorage.getItem('token');
        try {
            const { data } = await fetchJson(buildApiUrl(`/api/admin/reports/${reportId}/evidence`), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (data.success) {
                setSelectedEvidence(data.data);
                setEvidenceModalOpen(true);
            }
        } catch (err) {
            console.error('Error fetching evidence:', err);
            alert('Evidence not found or error loading.');
        }
    };

    const handleApproveAction = async (reportId) => {
        if (!window.confirm('Are you sure you want to APPROVE this safety action? This will execute the permanent ban.')) return;

        const token = localStorage.getItem('token');
        try {
            await fetchJson(buildApiUrl(`/api/admin/reports/${reportId}/approve`), {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            openSuccessModal('Action Approved and Executed.');
            // Update UI
            setReports(prevReports =>
                prevReports.map(report =>
                    report._id === reportId
                        ? { ...report, status: 'resolved', approvalStatus: 'approved' }
                        : report
                )
            );
        } catch (err) {
            console.error('Approval failed:', err);
            openErrorModal('Failed to approve action: ' + err.message);
        }
    };

    // Confirm Action Handler
    const handleConfirmAction = async () => {
        setIsSubmitting(true);
        const { type, reportId, userId, postId, inputValue } = modalConfig;
        const token = localStorage.getItem('token');

        try {
            if (type === 'success' || type === 'info') {
                closeModal();
                return;
            }

            if (type === 'resolve' || type === 'dismiss') {
                const newStatus = type === 'resolve' ? 'resolved' : 'rejected';
                await fetchJson(buildApiUrl(`/api/admin/reports/${reportId}/status`), {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ status: newStatus })
                });

                // Optimistic Update
                setReports(prevReports =>
                    prevReports.map(report =>
                        report._id === reportId
                            ? { ...report, status: newStatus }
                            : report
                    )
                );
            } else if (type === 'suspend') {
                await fetchJson(buildApiUrl(`/api/admin/users/${userId}/suspend`), {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ reason: inputValue })
                });
                openSuccessModal('User suspended successfully');
            } else if (type === 'delete') {
                await fetchJson(buildApiUrl(`/api/admin/users/${userId}/delete`), {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                openSuccessModal('User deleted successfully');
                setReports(prevReports => prevReports.filter(r => r.reported?._id !== userId));
            } else if (type === 'deletePost') {
                await fetchJson(buildApiUrl(`/api/admin/posts/${postId}`), {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                openSuccessModal('Post deleted successfully');
                // Backend auto-resolves reports for this post
                setReports(prevReports =>
                    prevReports.map(report =>
                        report.contentId === postId
                            ? {
                                ...report,
                                status: 'resolved',
                                post: null, // Remove post data locally since it's deleted
                                moderatorNotes: 'Post deleted by admin'
                            }
                            : report
                    )
                );
            }
        } catch (err) {
            console.error('Action failed:', err);
            openErrorModal('Failed to perform action. Please try again.');
        } finally {
            if (type !== 'success' && type !== 'info' && type !== 'allow_error') {
                setIsSubmitting(false);
                closeModal();
            } else {
                setIsSubmitting(false);
            }
        }
    };

    const getStatusBadge = (report) => {
        if (report.status === 'pending' && report.supervisorApprovalRequired && report.approvalStatus === 'pending') {
            return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200"><FiShield className="mr-1" /> Pending Approval</span>;
        }
        switch (report.status) {
            case 'resolved': return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><FiCheck className="mr-1" /> Resolved</span>;
            case 'rejected': return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"><FiSlash className="mr-1" /> Dismissed</span>;
            default: return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"><FiClock className="mr-1" /> Pending</span>;
        }
    };

    return (
        <AdminLayout>
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Content Moderation & Reports</h1>
                        <p className="text-gray-600">Review and manage user reports.</p>
                    </div>
                    <div className="flex space-x-2">
                        <select
                            className="border border-gray-300 rounded-md shadow-sm p-2 text-sm focus:ring-indigo-500 focus:border-indigo-500"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        >
                            <option value="pending">Pending Review</option>
                            <option value="resolved">Resolved</option>
                            <option value="rejected">Dismissed</option>
                            <option value="all">All Reports</option>
                        </select>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600"></div>
                    </div>
                ) : reports.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-sm p-12 text-center border border-gray-200">
                        <FiAlertTriangle className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No reports found</h3>
                        <p className="mt-1 text-sm text-gray-500">Good news! There are no reports matching your criteria.</p>
                    </div>
                ) : (
                    <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
                        <ul className="divide-y divide-gray-200">
                            {reports.map((report) => (
                                <li key={report._id} className={`p-6 hover:bg-gray-50 transition-colors ${report.priority >= 100 ? 'border-2 border-red-500 bg-red-50' : ''}`}>
                                    <div className="flex flex-col md:flex-row md:items-start md:justify-between space-y-4 md:space-y-0">
                                        <div className="flex-1 min-w-0 pr-4">
                                            {/* Header: Status | Time | Reason | Type */}
                                            <div className="flex items-center flex-wrap gap-2 mb-2">
                                                {getStatusBadge(report)}
                                                <span className="text-sm text-gray-500 flex items-center">
                                                    {new Date(report.createdAt).toLocaleString()}
                                                </span>
                                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700 uppercase border border-red-100">
                                                    {report.reason?.replace('_', ' ')}
                                                </span>
                                                {/* Type Badge */}
                                                <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase border ${report.type === 'post'
                                                    ? 'bg-blue-50 text-blue-700 border-blue-100'
                                                    : report.type === 'message'
                                                        ? 'bg-indigo-50 text-indigo-700 border-indigo-100'
                                                        : 'bg-purple-50 text-purple-700 border-purple-100'
                                                    }`}>
                                                    {report.type}
                                                </span>
                                            </div>

                                            <div className="space-y-3">
                                                {/* Reporter / Reported Info */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                                    <div>
                                                        <span className="font-medium text-gray-700">Reporter: </span>
                                                        {report.reporter ? (
                                                            <div className="flex flex-col">
                                                                <Link to={`/admin/users?search=${report.reporter.email}`} className="text-indigo-600 hover:text-indigo-900 hover:underline">
                                                                    {report.reporter.firstName} {report.reporter.lastName} {report.reporter.accountStatus === 'permanently_deleted' && <span className="text-red-500 font-semibold text-xs ml-1">(Deleted Account)</span>}
                                                                </Link>
                                                                <span className="text-xs text-gray-500">{report.reporter.email}</span>
                                                                <span className="text-xs text-gray-400">ID: {report.reporter._id}</span>
                                                            </div>
                                                        ) : (
                                                            <span className="italic text-gray-400">Unknown</span>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <span className="font-medium text-gray-900">Reported User: </span>
                                                        {report.targetUser ? (
                                                            <div className="flex flex-col">
                                                                <Link to={`/admin/users?search=${report.targetUser.email}`} className="text-indigo-600 hover:text-indigo-900 hover:underline">
                                                                    {report.targetUser.firstName} {report.targetUser.lastName} {report.targetUser.accountStatus === 'permanently_deleted' && <span className="text-red-500 font-semibold text-xs ml-1">(Deleted Account)</span>}
                                                                </Link>
                                                                <span className="text-xs text-gray-500">{report.targetUser.email}</span>
                                                                <span className="text-xs text-gray-400">ID: {report.targetUser._id}</span>
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-400 italic">User deleted</span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* User Description */}
                                                <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 text-sm text-gray-700 italic border-l-4 border-l-gray-300">
                                                    "{report.description}"
                                                </div>

                                                {/* Reported Content (Post) */}
                                                {report.type === 'post' && (
                                                    <div className="mt-2 border border-gray-200 rounded-lg p-4 bg-white">
                                                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Reported Content</h4>
                                                        {report.post ? (
                                                            <div className="space-y-2">
                                                                {report.post.content && (
                                                                    <p className="text-gray-900 text-sm whitespace-pre-wrap">{report.post.content}</p>
                                                                )}
                                                                {report.post.image && (
                                                                    <div className="mt-2">
                                                                        <img
                                                                            src={report.post.image.startsWith('http') ? report.post.image : `${buildApiUrl(report.post.image)}`}
                                                                            alt="Reported content"
                                                                            className="max-h-48 rounded-md object-contain border border-gray-100"
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-gray-500 italic flex items-center">
                                                                <FiAlertTriangle className="mr-2" />
                                                                Content no longer available (Post deleted)
                                                            </p>
                                                        )}

                                                        {/* EVIDENCE BUTTON */}
                                                        {report.actionTaken && (
                                                            <div className="mt-3 pt-3 border-t border-gray-100">
                                                                <button
                                                                    onClick={() => handleViewEvidence(report._id)}
                                                                    className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center"
                                                                >
                                                                    <FiEye className="mr-1" /> View Preserved Evidence
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Reported Content (Message) */}
                                                {report.type === 'message' && (
                                                    <div className="mt-2 border border-gray-200 rounded-lg p-4 bg-white">
                                                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Reported Message</h4>
                                                        {report.messageContent ? (
                                                            <div className="space-y-2">
                                                                {report.messageContent.content && (
                                                                    <p className="text-gray-900 text-sm whitespace-pre-wrap">{report.messageContent.content}</p>
                                                                )}
                                                                {((report.messageContent.type === 'image') || (report.messageContent.image)) && report.messageContent.image && (
                                                                    <div className="mt-2">
                                                                        <img
                                                                            src={report.messageContent.image.startsWith('http') ? report.messageContent.image : `${buildApiUrl(report.messageContent.image)}`}
                                                                            alt="Reported message"
                                                                            className="max-h-48 rounded-md object-contain border border-gray-100"
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-gray-500 italic flex items-center">
                                                                <FiAlertTriangle className="mr-2" />
                                                                Content no longer available
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex flex-col space-y-2 md:ml-4 min-w-[160px] flex-shrink-0">
                                            {/* Action Buttons */}
                                            {report.status === 'pending' && report.supervisorApprovalRequired ? (
                                                <div className="flex flex-col space-y-2">
                                                    <div className="text-xs text-orange-600 font-bold text-center mb-1">
                                                        Needs Approval
                                                    </div>
                                                    <button
                                                        onClick={() => handleApproveAction(report._id)}
                                                        className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 shadow-sm transition-colors"
                                                    >
                                                        <FiCheck className="mr-2 h-4 w-4" />
                                                        Approve
                                                    </button>
                                                    <button
                                                        onClick={() => handleViewEvidence(report._id)}
                                                        className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                                    >
                                                        Review
                                                    </button>
                                                </div>
                                            ) : report.status === 'pending' && (report.priority >= 100 || report.reason === 'child_safety') ? (
                                                <div className="flex flex-col space-y-2">
                                                    <button
                                                        onClick={() => openSafetyModal(report)}
                                                        className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 shadow-sm transition-colors"
                                                    >
                                                        <FiAlertTriangle className="mr-2 h-4 w-4" />
                                                        Take Action
                                                    </button>
                                                </div>
                                            ) : report.status === 'pending' ? (
                                                <div className="flex flex-col space-y-2">
                                                    <button
                                                        onClick={() => openStatusModal(report._id, 'resolved')}
                                                        className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 shadow-sm transition-colors"
                                                    >
                                                        <FiCheck className="mr-2 h-4 w-4" />
                                                        Resolve
                                                    </button>
                                                    <button
                                                        onClick={() => openStatusModal(report._id, 'rejected')}
                                                        className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 shadow-sm transition-colors"
                                                    >
                                                        <FiSlash className="mr-2 h-4 w-4" />
                                                        Dismiss
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col items-center justify-center p-3 border-2 border-dashed border-gray-100 rounded-lg bg-gray-50">
                                                    <span className="text-sm font-medium text-gray-500">
                                                        Action taken
                                                    </span>
                                                    <span className={`text-xs font-bold mt-1 ${report.status === 'resolved' ? 'text-green-600' : 'text-gray-500'}`}>
                                                        {report.status === 'resolved' ? 'RESOLVED' : 'DISMISSED'}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Critical Actions */}
                                            {/* Only show direct delete actions if NOT pending approval (since approval handles it)
                                                AND if status is not resolved/dismissed effectively? 
                                                Actually, keep them available for manual override if needed, but maybe hide for pending approval specifically
                                                to force the approval flow. */}
                                            {!report.supervisorApprovalRequired && (
                                                <div className="pt-3 border-t border-gray-100 mt-1 flex flex-col space-y-2">
                                                    {/* Delete Post Button */}
                                                    {report.type === 'post' && report.post && (
                                                        <button
                                                            onClick={() => openDeletePostModal(report.post._id, report._id)}
                                                            className="inline-flex items-center justify-center px-3 py-2 border border-transparent text-xs font-medium rounded text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none transition-colors mb-1"
                                                        >
                                                            <FiTrash2 className="mr-1.5 h-3 w-3" />
                                                            Delete Post
                                                        </button>
                                                    )}

                                                    {report.reported && (
                                                        <>
                                                            <button
                                                                onClick={() => openSuspendModal(report.reported._id)}
                                                                className="inline-flex items-center justify-center px-3 py-2 border border-transparent text-xs font-medium rounded text-orange-700 bg-orange-100 hover:bg-orange-200 focus:outline-none transition-colors"
                                                            >
                                                                Suspend User
                                                            </button>
                                                            <button
                                                                onClick={() => openDeleteModal(report.reported._id)}
                                                                className="inline-flex items-center justify-center px-3 py-2 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none transition-colors"
                                                            >
                                                                Ban & Delete
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <ConfirmationModal
                isOpen={modalConfig.isOpen}
                onClose={closeModal}
                onConfirm={handleConfirmAction}
                title={modalConfig.title}
                message={modalConfig.message}
                confirmText={modalConfig.confirmText}
                isDestructive={modalConfig.isDestructive}
                isLoading={isSubmitting}
                showInput={modalConfig.showInput}
                inputPlaceholder={modalConfig.inputPlaceholder}
                inputValue={modalConfig.inputValue}
                onInputChange={(val) => setModalConfig(prev => ({ ...prev, inputValue: val }))}
                cancelText={modalConfig.cancelText}
            />

            <SafetyActionModal
                isOpen={safetyModalOpen}
                onClose={closeModal}
                onConfirm={handleSafetyAction}
                report={selectedSafeyReport}
                isSubmitting={isSubmitting}
            />

            <EvidenceModal
                isOpen={evidenceModalOpen}
                onClose={closeModal}
                evidence={selectedEvidence}
            />
        </AdminLayout >
    );
};

export default AdminReports;
